/**
 * Convites — o CÓDIGO DO GRUPO.
 *
 * Um código por viagem, reutilizável: todo mundo entra com o mesmo. Quem
 * administra escolhe a validade ao gerar (1 dia · 7 dias · 30 dias · até o
 * fim da viagem), vê quantas pessoas já entraram, copia, compartilha e revoga
 * quando quiser. Gerar um novo revoga o anterior — nunca existem dois.
 *
 * O link leva o próprio código (`/convite?code=…`), então pode ser
 * reconstruído a qualquer hora — diferente do convite pessoal, cujo token só
 * aparecia uma vez.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useOnline } from '../../sync/useOnline';
import { Icon } from '../../ui/icons';
import { Card, ErrorState, SectionLabel, Skeleton } from '../../ui/primitives';
import { createGroupInvite, getGroupInvite, type GroupInviteExpiresIn, GrupoError, revokeTripInvite } from './api';
import { OPCOES_DE_VALIDADE, textoDeCompartilhamento, validadeDoCodigo } from './convites';
import { rotaNegado } from './permissaoLabels';

function mensagemDe(erro: unknown, padrao: string): string {
  return erro instanceof GrupoError ? erro.message : padrao;
}

export function ConvitesScreen({
  tripId,
  tripName,
  permissoes,
}: {
  tripId: string;
  tripName: string;
  permissoes: string[];
}) {
  const cliente = useQueryClient();
  const online = useOnline();

  const [validade, setValidade] = useState<GroupInviteExpiresIn>('7d');
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const atual = useQuery({ queryKey: ['convites', tripId, 'grupo'], queryFn: () => getGroupInvite(tripId) });
  const invalidar = () => cliente.invalidateQueries({ queryKey: ['convites', tripId] });

  const gerar = useMutation({
    mutationFn: () => createGroupInvite(tripId, validade),
    onSuccess: () => {
      setAviso(null);
      setConfirmando(false);
      void invalidar();
    },
  });
  const revogar = useMutation({
    mutationFn: (id: string) => revokeTripInvite(tripId, id),
    onSuccess: () => {
      setConfirmando(false);
      void invalidar();
    },
  });

  if (!permissoes.includes('manage_members')) {
    return <Navigate to={rotaNegado('manage_members')} replace />;
  }

  const grupo = atual.data?.group ?? null;
  const agora = Date.now();
  const podeCompartilhar = typeof navigator.share === 'function';

  async function copiar(texto: string, oQue: string) {
    try {
      await navigator.clipboard.writeText(texto);
      setAviso(`${oQue} copiado.`);
    } catch {
      setAviso('Não foi possível copiar automaticamente. Selecione e copie.');
    }
  }

  function compartilhar() {
    if (!grupo) return;
    void navigator
      .share({
        title: 'Convite para a viagem',
        text: textoDeCompartilhamento({ tripName, code: grupo.code, url: grupo.url }),
        url: grupo.url,
      })
      .catch(() => undefined);
  }

  return (
    <section className="grupo">
      <header className="fin-nova__header">
        <h1 className="viagens__titulo">Código do grupo</h1>
      </header>

      {!online && (
        <Card variant="chip">
          <p className="auth-note" role="status">
            Sem conexão. Gerar e revogar o código só funciona online.
          </p>
        </Card>
      )}

      {atual.isLoading && <Skeleton height={140} />}
      {atual.isError && (
        <ErrorState title="Não foi possível carregar" description="Verifique a conexão e tente de novo." onRetry={() => void atual.refetch()} />
      )}

      {atual.isSuccess && grupo && (
        <Card variant="surf" className="convites__criado">
          <SectionLabel>CÓDIGO</SectionLabel>
          <p className="convites__codigo" data-testid="codigo-do-grupo">{grupo.code}</p>
          <p className="auth-note">
            {validadeDoCodigo(grupo.expiresAt, agora)} · {grupo.uses === 0 ? 'ninguém entrou ainda' : grupo.uses === 1 ? '1 pessoa entrou' : `${grupo.uses} pessoas entraram`}
          </p>

          <SectionLabel>LINK</SectionLabel>
          <input readOnly className="convites__link" value={grupo.url} aria-label="Link do código do grupo" onFocus={(e) => e.currentTarget.select()} />

          <div className="fin__ctas">
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => void copiar(grupo.code, 'Código')}>
              Copiar código
            </button>
            <button type="button" className="nv-btn nv-btn--secondary" onClick={() => void copiar(grupo.url, 'Link')}>
              Copiar link
            </button>
          </div>
          {podeCompartilhar && (
            <button type="button" className="nv-btn nv-btn--primary nv-btn--block" onClick={compartilhar}>
              <Icon name="compartilhar" size={18} />
              Compartilhar
            </button>
          )}
          {aviso && (
            <p className="auth-note" role="status">
              {aviso}
            </p>
          )}
          <p className="auth-note">Qualquer pessoa com este código entra na viagem como participante, quantas vezes for preciso, até a validade ou até você revogar.</p>

          {!confirmando ? (
            <button type="button" className="nv-btn nv-btn--ghost-danger nv-btn--block" disabled={!online} onClick={() => setConfirmando(true)}>
              Revogar código
            </button>
          ) : (
            <>
              <p className="auth-note">Quem tiver este código ou link não conseguirá mais entrar. Quem já entrou continua na viagem.</p>
              <div className="fin__ctas">
                <button type="button" className="nv-btn nv-btn--secondary" onClick={() => setConfirmando(false)}>
                  Manter
                </button>
                <button type="button" className="nv-btn nv-btn--danger" disabled={!online || revogar.isPending} onClick={() => revogar.mutate(grupo.id)}>
                  Revogar
                </button>
              </div>
              {revogar.isError && (
                <p className="auth-error" role="alert">
                  {mensagemDe(revogar.error, 'Não foi possível revogar agora.')}
                </p>
              )}
            </>
          )}
        </Card>
      )}

      {atual.isSuccess && (
        <>
          <SectionLabel>{grupo ? 'GERAR UM NOVO' : 'GERAR O CÓDIGO'}</SectionLabel>
          <Card>
            <p className="auth-note" style={{ marginTop: 0 }}>
              {grupo ? 'Gerar um novo código revoga o atual na hora.' : 'A viagem ainda não tem código. Escolha por quanto tempo ele vale.'}
            </p>
            <div className="chip-row" role="radiogroup" aria-label="Validade">
              {OPCOES_DE_VALIDADE.map((o) => (
                <button
                  type="button"
                  key={o.valor}
                  role="radio"
                  aria-checked={validade === o.valor}
                  className={validade === o.valor ? 'pick pick--on' : 'pick'}
                  onClick={() => setValidade(o.valor)}
                >
                  {o.rotulo}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`nv-btn nv-btn--block ${grupo ? 'nv-btn--secondary' : 'nv-btn--primary'}`}
              style={{ marginTop: 10 }}
              disabled={!online || gerar.isPending}
              onClick={() => gerar.mutate()}
            >
              {gerar.isPending ? 'Gerando…' : grupo ? 'Gerar novo código' : 'Gerar código'}
            </button>
            {gerar.isError && (
              <p className="auth-error" role="alert">
                {mensagemDe(gerar.error, 'Não foi possível gerar o código agora.')}
              </p>
            )}
          </Card>
        </>
      )}
    </section>
  );
}
